// This is the updated ProductDetailPage.jsx
import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTier } from "../../context/TierContext";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import DosageSelector from "../../components/product/DosageSelector";
import SupplyMonthSelector from "../../components/product/SupplyMonthSelector";
import * as productService from "../../services/productService";
import { getAvailableSupplyKeys, resolveBasisSupplyMonths, toPerMonthRate } from "../../utils/supplyTerms";
import { DOCTOR_NOW_DOSAGE_NOTICE, canCustomerSelectDosage } from "../../utils/doctorNowDosageNotice";
import styles from "./ProductDetailPage.module.css";

// Render cents as dollars, showing the two-decimal remainder only when it is non-zero
// (8690 -> "$86.90", 6900 -> "$69"). Keeps modifier-applied tiers penny-accurate against
// the charge while whole-dollar tiers read exactly as before. Mirrors CheckoutPage's format.
const formatCents = (cents) => (cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`);

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Read only to re-run the fetch below. The PRICE never comes from here — it comes back from
  // the server as `userTier`; api.js is what puts the preview on the request.
  const { tierType } = useTier();

  const [product, setProduct] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedDosage, setSelectedDosage] = useState(null);
  const [selectedSupplyMonths, setSelectedSupplyMonths] = useState(null);
  const [viewerTier, setViewerTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Determine default view based on screen width (minimized on mobile)
  const defaultDosageView = useMemo(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return "minimized";
    }
    return "expanded";
  }, []);

  // Card 53600: hoisted out of the effect so the failure branch can RE-ISSUE this exact
  // request from a Try again control, rather than asking the member to reload the page.
  // `tierType` is in the deps because this loader was keyed on [slug] alone: switching tier in
  // the Test Dashboard and coming back to a still-mounted product page would otherwise show the
  // previous tier's prices — the exact "looks like a broken server gate" symptom.
  const loadProduct = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const previewParam = searchParams.get("preview");
      const response = await productService.getProductBySlug(slug, { preview: previewParam });
      setProduct(response.product);
      // The tier the server priced this product for — the member’s real membership
      // tier. Never guess it client-side; the prices on screen are the server’s.
      setViewerTier(response.userTier || null);
      if (response.product?.pricing?.length > 0) {
        const popular = response.product.pricing.find((t) => t.isPopular);
        setSelectedTier(popular || response.product.pricing[0]);
      }
    } catch (error) {
      console.error("Failed to load product:", error);
      // 404 is this route’s ONE legitimate "no such product" answer (productController.js:217
      // throws createError.notFound('Product')), so it must keep rendering "Product not found".
      // Every other failure — 5xx, 403, network — is a LOAD FAILURE and must never be reported
      // to the member as the product not existing. Same split ProductsPage.jsx:32 already makes.
      if (error?.status !== 404) {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tierType]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Set default dosage when product loads
  useEffect(() => {
    if (product?.dosages?.length > 0) {
      const dosageIdFromUrl = searchParams.get("dosage");
      if (dosageIdFromUrl) {
        const dosageFromUrl = product.dosages.find((d) => d.id === dosageIdFromUrl);
        if (dosageFromUrl) {
          setSelectedDosage(dosageFromUrl);
          return;
        }
      }

      try {
        const stored = sessionStorage.getItem("selectedDosage");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.productSlug === slug) {
            const dosageFromStorage = product.dosages.find((d) => d.id === parsed.dosageId);
            if (dosageFromStorage) {
              setSelectedDosage(dosageFromStorage);
              return;
            }
          }
        }
      } catch {
        // Ignore sessionStorage errors
      }

      const defaultDosage = product.dosages.find((d) => d.isDefault) || product.dosages[0];
      setSelectedDosage(defaultDosage);
    } else {
      setSelectedDosage(null);
    }
  }, [product?.id, product?.dosages, searchParams, slug]);

  // Pre-select the term the product advertises — its starting-at basis — so the
  // "Starting at $X/mo" quoted on the catalog and the term selected here refer to the
  // same supply. Falls back to the shortest available term when the basis is `all`,
  // absent (STARTING_AT_BASIS_V2=false), or not offered by this dosage. A term the
  // member already chose is kept when the new dosage still offers it.
  useEffect(() => {
    if (!selectedDosage?.supplyPricing) return;
    const available = getAvailableSupplyKeys(selectedDosage)
      .map(Number)
      .sort((a, b) => a - b);
    if (available.length === 0 || available.includes(selectedSupplyMonths)) return;
    setSelectedSupplyMonths(resolveBasisSupplyMonths(product, selectedDosage) ?? available[0]);
  }, [selectedDosage?.id, product?.id]);

  const handleDosageSelect = (dosage) => {
    setSelectedDosage(dosage);
  };

  // Does THIS viewer actually carry a product price modifier?
  //
  // `viewerTier` is the server's own `userTier`, and productController sets it to
  // `tierOpts.tierType || null` where getTierOpts returns `{}` unless the user has an ACTIVE
  // customMembershipTierId. So non-null <=> the viewer carries that tier's modifier. This page
  // has no `pricingBreakdown` to compare against (CheckoutPage does, and uses that instead).
  //
  // This matters because the two server maps disagree only for `access`: the effective map is
  // built from the VIEWER-AGNOSTIC per-tier modifier map, so it bakes in Oval Access's +10% for
  // everyone, including signed-out visitors -- while pricingEngine charges the RAW rate to anyone
  // without a tier. Oval One and Oval Plus carry modifier 0, so their two maps are byte-identical
  // and picking `effective` for them is a no-op. No modifier math in the browser.
  const hasTierPriceModifier = Boolean(viewerTier);

  // Get the effective price for the selected dosage and supply months, for the tier the
  // SERVER priced this product for, using whichever map is charge-exact for this viewer.
  const getEffectivePrice = () => {
    if (!selectedDosage) return null;
    const sp = (hasTierPriceModifier ? selectedDosage.supplyPricingEffective : selectedDosage.supplyPricing)
      ?? selectedDosage.supplyPricing;
    if (sp && typeof sp === "object") {
      // Quote per INTERNAL month so the 60 Day term stops advertising two months of
      // medication at its two-month total — a no-op for keys "3"/"6"/"12". DISPLAY ONLY:
      // the charge stays `stored × Number(key)`. The legacy flat column below carries no
      // supply term, so it is already a per-month rate and is deliberately NOT divided.
      const key = String(selectedSupplyMonths);
      const priceData = sp[key];
      if (priceData && typeof priceData === "object") {
        const tier = viewerTier || "access";
        return toPerMonthRate(priceData[tier] ?? priceData.access ?? 0, key);
      }
      if (typeof priceData === "number" && priceData > 0) return toPerMonthRate(priceData, key);
    }
    return selectedDosage.price || null;
  };

  const handleStartIntake = () => {
    if (!user) {
      navigate("/login");
      return;
    }

    let checkoutUrl = `/checkout?product=${product.slug}`;

    if (selectedTier?.id) {
      checkoutUrl += `&tier=${selectedTier.id}`;
    }

    if (selectedDosage?.id) {
      checkoutUrl += `&dosage=${selectedDosage.id}`;

      try {
        sessionStorage.setItem("selectedDosage", JSON.stringify({
          productSlug: product.slug,
          dosageId: selectedDosage.id,
          dosageAmount: selectedDosage.amount,
          dosagePrice: selectedDosage.price
        }));
      } catch {
        // Ignore sessionStorage errors
      }
    }

    // Hand the chosen term to checkout for EVERY duration. This was `> 1`, which
    // silently dropped a 60 Day (key "1") selection — checkout then fell back to its
    // own 3-month default, so a member who picked 60 Day landed on a 3-month order.
    if (selectedSupplyMonths > 0) {
      checkoutUrl += `&supplyMonths=${selectedSupplyMonths}`;
    }

    // Carry preview param through so intake/checkout can also bypass status checks
    const previewParam = searchParams.get("preview");
    if (previewParam) {
      checkoutUrl += `&preview=${previewParam}`;
    }

    navigate(checkoutUrl);
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (loadError) {
    return (
      <div className={styles.notFound} role="alert">
        <h2>We could not load this product</h2>
        <p>Something went wrong while loading this page. The product is still available.</p>
        <Button onClick={loadProduct}>Try again</Button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.notFound}>
        <h2>Product not found</h2>
        <Link to="/categories">
          <Button>Back to Catalog</Button>
        </Link>
      </div>
    );
  }

  const isPreview = searchParams.get("preview") === product?.id;
  const hasPricingTiers = product.pricing?.length > 0;
  const hasSupplyPricing = selectedDosage?.supplyPricing && Object.keys(selectedDosage.supplyPricing).length > 0;
  const effectivePrice = getEffectivePrice();
  // Card 54405: a Doctor Now clinician picks the dose, so the selector and the dosage amount are
  // hidden and the notice is shown instead. `selectedDosage` is still resolved above and still
  // handed to checkout, so pricing is unchanged.
  const isDosageSelectable = canCustomerSelectDosage(product);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb}>
          <Link to="/categories">Categories</Link>
          <span>/</span>
          <Link to={`/categories/${product.category?.slug}`}>{product.category?.name}</Link>
          <span>/</span>
          <span>{product.name}</span>
        </nav>

        <div className={styles.layout}>
          {/* Product Info */}
          <div className={styles.main}>
            <div className={styles.imageSection}>
              {product.primaryImage ? (
                <img loading="lazy" src={product.primaryImage} alt={product.name} />
              ) : (
                <div className={styles.placeholder}></div>
              )}
            </div>

            <div className={styles.content}>
              <h1>{product.name}</h1>
              {product.isComingSoon && (
                <div className={styles.comingSoonBadge}>Coming Soon</div>
              )}
              {product.isOutOfStock && (
                <div className={styles.outOfStockBadge}>Out of Stock</div>
              )}
              {product.genericName && (
                <span className={styles.genericName}>{product.genericName}</span>
              )}
              {product.startingAtPrice > 0 && (
                <div className={styles.startingAtPrice}>
                  Starting at ${Math.ceil(product.startingAtPrice / 100)}/mo
                </div>
              )}
              <p className={styles.description}>{product.description}</p>

              {product.howItWorks && (
                <section className={styles.section}>
                  <h2>How It Works</h2>
                  <p>{product.howItWorks}</p>
                </section>
              )}

              {!isDosageSelectable && (
                <section className={styles.section}>
                  <h2>Dosage</h2>
                  <p>{DOCTOR_NOW_DOSAGE_NOTICE}</p>
                </section>
              )}

              {isDosageSelectable && product.dosages?.length > 0 && (
                <section className={styles.section}>
                  <DosageSelector
                    dosages={product.dosages}
                    selectedDosage={selectedDosage}
                    onSelect={handleDosageSelect}
                    selectedSupplyMonths={selectedSupplyMonths}
                    tierType={viewerTier}
                    hasTierPriceModifier={hasTierPriceModifier}
                    defaultView={defaultDosageView}
                  />
                </section>
              )}

              {product.sideEffects?.length > 0 && (
                <section className={styles.section}>
                  <h2>Possible Side Effects</h2>
                  <p className={styles.disclaimer}>
                    Common side effects may include:
                  </p>
                  <ul className={styles.sideEffects}>
                    {product.sideEffects.map((effect, i) => (
                      <li key={i}>{effect}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>

          {/* Pricing Sidebar */}
          <aside className={styles.sidebar}>
            <Card className={styles.pricingCard}>
              {hasPricingTiers ? (
                <>
                  <h2>Select Your Plan</h2>
                  <div className={styles.tiers}>
                    {product.pricing.map((tier) => (
                      <button
                        key={tier.id}
                        className={`${styles.tier} ${selectedTier?.id === tier.id ? styles.selected : ""}`}
                        onClick={() => setSelectedTier(tier)}
                      >
                        {tier.isPopular && <span className={styles.popular}>Most Popular</span>}
                        <div className={styles.tierName}>{tier.name}</div>
                        <div className={styles.tierPrice}>{tier.priceFormatted}</div>
                        {tier.originalPriceFormatted && (
                          <div className={styles.tierOriginal}>
                            <s>{tier.originalPriceFormatted}</s>
                            <span className={styles.savings}>Save {tier.savings}</span>
                          </div>
                        )}
                        <div className={styles.tierDuration}>{tier.durationLabel} supply</div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h2>Your Selection</h2>
                  {isDosageSelectable && selectedDosage && (
                    <div className={styles.selectedDosageInfo}>
                      <span className={styles.dosageLabel}>{selectedDosage.amount}</span>
                      {selectedDosage.description && (
                        <span className={styles.dosageDesc}>{selectedDosage.description}</span>
                      )}
                    </div>
                  )}
                  {/* `selectedSupplyMonths` is null until the basis-term effect above runs; passing
                      undefined lets the selector's own default stand in, so React no longer logs
                      "`value` prop on select should not be null" on every product page (card 54405). */}
                  {hasSupplyPricing && (
                    <SupplyMonthSelector
                      pricePerMonth={selectedDosage?.price || 0}
                      supplyPricing={selectedDosage.supplyPricing}
                      supplyPricingEffective={selectedDosage.supplyPricingEffective}
                      supplyPricingMeta={selectedDosage.supplyPricingMeta}
                      selectedMonths={selectedSupplyMonths ?? undefined}
                      tierType={viewerTier}
                      hasTierPriceModifier={hasTierPriceModifier}
                      onChange={setSelectedSupplyMonths}
                    />
                  )}
                  {effectivePrice > 0 && (
                    <div className={styles.priceDisplay}>
                      {formatCents(effectivePrice)}/mo
                    </div>
                  )}
                </>
              )}

              {product.isComingSoon && !isPreview ? (
                <Button fullWidth size="large" disabled className={styles.comingSoonButton}>
                  Coming Soon
                </Button>
              ) : product.isOutOfStock && !isPreview ? (
                <Button fullWidth size="large" disabled>
                  Out of Stock
                </Button>
              ) : (
                <Button onClick={handleStartIntake} fullWidth size="large">
                  Get Started
                </Button>
              )}

              <p className={styles.rxNote}>
                Requires online consultation with a licensed physician.
              </p>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
